import React, { useState } from 'react';
import i18n from 'i18n-js';
import { MomentInput } from 'moment';

import { localTime } from '@helpers';
import { font } from '@styles/fonts';

import { CountData, TimelineDataWithLoadingState, TimelineProductionBatchesAndCounts } from '@interfaces/timeline';

import { LegendRules } from '@components/machines/components/timeline/calculations/index';
import { TimelineBlockTitle } from './TimelineBlockTitle';
import { COUNTS_CONFIG, TIMELINE_BLOCK_TITLE_CONFIG, TIMELINE_LINE_CONFIG } from '@components/machines/components/timeline/constants';
import TimelineShimmer from './TimelineShimmer';
import { getTimelineFontStyles } from '@components/machines/components/timeline/helpers';
import { ColorFunction } from '@styles/colors/types';

let lastDisplaySecondsProductionBatches: null | number = null;

export default (props: {
  C: ColorFunction;
  countsData: TimelineDataWithLoadingState<CountData[]>;
  blockWidth: number;
  startTime: MomentInput;
  rules: LegendRules;
  withLeftValueLimitation?: boolean;
  useIndexAsCountText?: boolean;
  withoutHairline?: boolean;
}) => {
  const { C, countsData, blockWidth, startTime, rules, withLeftValueLimitation, useIndexAsCountText, withoutHairline } = props;

  const [hoveredNcFileId, setHoveredNcFileId] = useState<number | null>(null);

  const countHairlineHeight =
    (COUNTS_CONFIG.container?.marginBottom || 0) +
    (font[TIMELINE_BLOCK_TITLE_CONFIG.fontType]?.lineHeight || 0) +
    (TIMELINE_BLOCK_TITLE_CONFIG.marginBottom || 0);

  const { fontStylesWeb } = getTimelineFontStyles();

  return (
    <div style={{ marginBottom: COUNTS_CONFIG.container.marginBottom }}>
      <TimelineBlockTitle C={C}>{i18n.t('Counts')}</TimelineBlockTitle>
      <div
        style={{
          display: 'flex',
          height: 5,
          width: blockWidth,
          borderRadius: 3,
          backgroundColor: C(TIMELINE_LINE_CONFIG.backgroundColor),
          flexDirection: 'row',
          position: 'relative',
        }}
      >
        {countsData.loaded ? (
          countsData?.data?.map((i, index) => {
            if (index === 0) {
              lastDisplaySecondsProductionBatches = null;
            }

            const elementSeconds = localTime(i.endedOn).diff(localTime(startTime), 's');
            const numberWidthPX = 12;
            const showNumber = lastDisplaySecondsProductionBatches
              ? Math.round((elementSeconds - lastDisplaySecondsProductionBatches) * rules.secondWidth) >= numberWidthPX
              : true;

            if (showNumber) {
              lastDisplaySecondsProductionBatches = elementSeconds;
            }

            const isHighlight = !hoveredNcFileId || hoveredNcFileId === i.ncFileId;

            const countLeft = rules.secondWidth * elementSeconds - 3;
            const countSize = 7;
            const countBackgroundColor = C(isHighlight ? COUNTS_CONFIG.colorHighlight : COUNTS_CONFIG.colorNotHighlight);
            const countShouldBeHidden =
              countLeft < -countSize || countLeft > blockWidth || (withLeftValueLimitation && (countLeft < 0 || countLeft > blockWidth));

            if (countShouldBeHidden) return null;

            return (
              <div
                key={`${i.endedOn}-${index}-countsData`}
                onMouseOver={() => {
                  const timer = setTimeout(() => {
                    setHoveredNcFileId(i.ncFileId);
                  }, 200);
                  return () => clearTimeout(timer);
                }}
                onMouseLeave={() => {
                  const timer = setTimeout(() => {
                    setHoveredNcFileId(null);
                  }, 200);
                  return () => clearTimeout(timer);
                }}
              >
                <div
                  style={{
                    width: countSize,
                    height: countSize,
                    backgroundColor: countBackgroundColor,
                    transform: 'rotate(45deg)',
                    position: 'absolute',
                    left: countLeft,
                    top: -1,
                  }}
                />
                {!withoutHairline && (
                  <div
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: countHairlineHeight - 1,
                      backgroundColor: countBackgroundColor,
                      left: countLeft + Math.floor(countSize / 2),
                      top: 5,
                      opacity: 0.3,
                    }}
                  />
                )}
                {showNumber && (
                  <div
                    id={`${i.endedOn}-countsData-${index}`}
                    style={{
                      width: numberWidthPX,
                      height: 16,
                      position: 'absolute',
                      left: rules.secondWidth * elementSeconds - numberWidthPX / 2,
                      top: 7,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        ...fontStylesWeb,
                        color: C(isHighlight ? 'Label/Light/Tertiary' : 'Gray/Light/3'),
                        userSelect: 'none',
                        textAlign: 'center',
                      }}
                    >
                      {useIndexAsCountText ? index + 1 : i.quantityDelta}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <TimelineShimmer />
        )}
      </div>
    </div>
  );
};
